import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Stack,
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Chip,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Tooltip,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EmailIcon from "@mui/icons-material/Email";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { Switch, FormControlLabel, MenuItem, Select } from "@mui/material";
import {
  getBrokerCompliance,
  downloadBrokerAuditPacket,
  getBrokerAuditPacketHistory,
  nudgeBrokerNonCompliant,
  getBrokerAuditSchedule,
  saveBrokerAuditSchedule,
} from "api/brokerApi";
import { unwrapList, unwrapObject } from "api/unwrap";

/**
 * /brokers/:id/compliance — per-broker audit-response screen.
 *
 * For one broker:
 *   • Top-of-page KPI cards: total drivers, fully compliant, non-compliant
 *   • Table of every driver servicing the broker with per-row missing items
 *
 * Use case: brokers periodically ask "send me proof your drivers servicing
 * us are up-to-date on our required trainings + policies." This screen is
 * the answer.
 */

const Kpi = ({ label, value, accent }) => (
  <Card variant="outlined" sx={{ borderRadius: 2, flex: 1, minWidth: 0 }}>
    <CardContent>
      <Typography variant="overline" color="text.secondary">{label}</Typography>
      <Typography variant="h3" sx={{ fontWeight: 700, color: accent || "#0f172a", mt: 1 }}>
        {value}
      </Typography>
    </CardContent>
  </Card>
);

export const BrokerCompliancePage = () => {
  const { brokerId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all | compliant | noncompliant
  const [downloading, setDownloading] = useState(false);
  const [nudging, setNudging] = useState(false);
  const [nudgeResult, setNudgeResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [schedule, setSchedule] = useState(null);
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);

  const loadSchedule = useCallback(async () => {
    if (!data?.brokerId) return;
    try {
      const res = await getBrokerAuditSchedule(data.brokerId);
      const s = unwrapObject(res);
      if (s) setSchedule(s);
    } catch {
      // Non-fatal
    }
  }, [data?.brokerId]);

  useEffect(() => {
    loadSchedule();
  }, [loadSchedule]);

  const handleSaveSchedule = async () => {
    if (!data?.brokerId || !schedule) return;
    setScheduleSaving(true);
    setError(null);
    try {
      const res = await saveBrokerAuditSchedule(data.brokerId, schedule);
      const s = unwrapObject(res);
      if (s) setSchedule(s);
    } catch (e) {
      setError(e?.message || "Save schedule failed");
    } finally {
      setScheduleSaving(false);
    }
  };

  const loadHistory = useCallback(async () => {
    if (!data?.brokerId) return;
    try {
      const res = await getBrokerAuditPacketHistory(data.brokerId);
      setHistory(unwrapList(res));
    } catch {
      // Non-fatal: history is informational
    }
  }, [data?.brokerId]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getBrokerCompliance(brokerId);
      setData(unwrapObject(res));
    } catch (e) {
      setError(e?.message || "Failed to load compliance report");
    } finally {
      setLoading(false);
    }
  }, [brokerId]);

  useEffect(() => {
    load();
  }, [load]);

  const filteredDrivers = useMemo(() => {
    const rows = data?.drivers || [];
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter === "compliant" && !r.fullyCompliant) return false;
      if (filter === "noncompliant" && r.fullyCompliant) return false;
      if (!q) return true;
      const hay = `${r.fullName || ""} ${r.email || ""} ${r.phone || ""} ${r.customerCompanyName || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [data, filter, search]);

  if (loading && !data) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack
      spacing={2}
      sx={{ p: { xs: 2, md: 3 }, backgroundColor: "#f8fafc", minHeight: "100%" }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Button
            variant="text"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/brokers")}
            sx={{ color: "#475569" }}
          >
            Brokers
          </Button>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: "#0f172a" }}>
              {data?.brokerName || "Broker"} Compliance
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Drivers servicing {data?.brokerCode} via active contracts.
              Required by this broker: {data?.requiredTrainingCount || 0} training{(data?.requiredTrainingCount || 0) === 1 ? "" : "s"}, {" "}
              {data?.requiredPolicyCount || 0} polic{(data?.requiredPolicyCount || 0) === 1 ? "y" : "ies"}.
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            disabled={!data?.brokerId || downloading}
            onClick={async () => {
              if (!data?.brokerId) return;
              setDownloading(true);
              setError(null);
              try {
                await downloadBrokerAuditPacket(data.brokerId, data.brokerCode);
                loadHistory();
              } catch (e) {
                setError(e?.message || "Audit packet download failed");
              } finally {
                setDownloading(false);
              }
            }}
            sx={{ backgroundColor: "#1F4E78" }}
          >
            {downloading ? "Building…" : "Audit packet"}
          </Button>
          <Button
            variant="outlined"
            startIcon={<EmailIcon />}
            disabled={!data?.brokerId || (data?.noncompliantDrivers ?? 0) === 0 || nudging}
            onClick={async () => {
              if (!data?.brokerId) return;
              const n = data?.noncompliantDrivers ?? 0;
              if (n === 0) return;
              if (!window.confirm(`Email all ${n} non-compliant driver${n === 1 ? "" : "s"} with what they're missing for ${data?.brokerCode}?`)) return;
              setNudging(true);
              setError(null);
              setNudgeResult(null);
              try {
                const res = await nudgeBrokerNonCompliant(data.brokerId);
                const r = unwrapObject(res);
                if (r) setNudgeResult(r);
              } catch (e) {
                setError(e?.message || "Nudge failed");
              } finally {
                setNudging(false);
              }
            }}
          >
            {nudging ? "Sending…" : `Nudge non-compliant (${data?.noncompliantDrivers ?? 0})`}
          </Button>
          <Button
            variant="outlined"
            startIcon={<ScheduleIcon />}
            onClick={() => setScheduleOpen((o) => !o)}
          >
            {schedule?.enabled ? `Auto-email: every ${schedule.cadenceDays}d` : "Auto-email: off"}
          </Button>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
            Refresh
          </Button>
        </Box>
      </Box>

      {scheduleOpen && schedule ? (
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Audit packet auto-email
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
              When enabled, the system emails the latest audit packet ZIP to the recipients below on the chosen cadence. Recipients can be comma-separated.
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!schedule.enabled}
                    onChange={(e) => setSchedule({ ...schedule, enabled: e.target.checked })}
                  />
                }
                label="Enabled"
              />
              <Select
                size="small"
                value={schedule.cadenceDays || 7}
                onChange={(e) => setSchedule({ ...schedule, cadenceDays: e.target.value })}
                sx={{ minWidth: 140 }}
              >
                <MenuItem value={1}>Daily</MenuItem>
                <MenuItem value={7}>Weekly</MenuItem>
                <MenuItem value={14}>Every 2 weeks</MenuItem>
                <MenuItem value={30}>Monthly</MenuItem>
                <MenuItem value={90}>Quarterly</MenuItem>
              </Select>
              <TextField
                size="small"
                placeholder="audit@modivcare.com, ops@..."
                value={schedule.recipients || ""}
                onChange={(e) => setSchedule({ ...schedule, recipients: e.target.value })}
                sx={{ flex: 1, minWidth: 280 }}
              />
              <Button
                variant="contained"
                onClick={handleSaveSchedule}
                disabled={scheduleSaving}
                sx={{ backgroundColor: "#1F4E78" }}
              >
                {scheduleSaving ? "Saving…" : "Save"}
              </Button>
            </Box>
            {schedule.lastSentAt || schedule.nextDueAt || schedule.lastStatus ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
                {schedule.lastSentAt ? `Last sent: ${new Date(schedule.lastSentAt).toLocaleString()}` : "Never sent"}
                {schedule.nextDueAt && schedule.enabled ? ` · Next: ${new Date(schedule.nextDueAt).toLocaleString()}` : ""}
                {schedule.lastStatus ? ` · Last status: ${schedule.lastStatus}` : ""}
              </Typography>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {nudgeResult ? (
        <Alert severity="success" onClose={() => setNudgeResult(null)}>
          Queued {nudgeResult.emailsSent} email{nudgeResult.emailsSent === 1 ? "" : "s"} to {nudgeResult.candidates} non-compliant driver{nudgeResult.candidates === 1 ? "" : "s"}
          {nudgeResult.skippedNoEmail > 0 ? ` · ${nudgeResult.skippedNoEmail} skipped (no email on file)` : ""}.
          Delivery happens in the background — any failures will show up in /admin/outbox and on the notification bell.
        </Alert>
      ) : null}

      {history.length > 0 ? (
        <Typography variant="caption" color="text.secondary">
          Last audit packet: {new Date(history[0].downloadedAtMs).toLocaleString()} ·{" "}
          {history[0].downloadedByName || "unknown user"} ·{" "}
          {history[0].driverCount} driver{history[0].driverCount === 1 ? "" : "s"}
          {history.length > 1 ? ` · ${history.length} previous download${history.length === 2 ? "" : "s"} on record` : ""}
        </Typography>
      ) : null}

      {error ? <Alert severity="error" onClose={() => setError(null)}>{error}</Alert> : null}

      <Box sx={{ display: "flex", gap: 2 }}>
        <Kpi label="Total drivers" value={data?.totalDrivers ?? 0} />
        <Kpi label="Compliant" value={data?.compliantDrivers ?? 0} accent="#16a34a" />
        <Kpi label="Non-compliant" value={data?.noncompliantDrivers ?? 0} accent={(data?.noncompliantDrivers ?? 0) > 0 ? "#dc2626" : "#0f172a"} />
      </Box>

      <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
        <TextField
          size="small"
          placeholder="Search by name / email / phone / company"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 280 }}
        />
        {[
          { k: "all", label: "All" },
          { k: "noncompliant", label: "Non-compliant" },
          { k: "compliant", label: "Compliant" },
        ].map((b) => (
          <Chip
            key={b.k}
            size="small"
            label={b.label}
            onClick={() => setFilter(b.k)}
            sx={{
              backgroundColor: filter === b.k ? "#1F4E78" : "#f1f5f9",
              color: filter === b.k ? "#ffffff" : "#475569",
              fontWeight: filter === b.k ? 700 : 500,
              cursor: "pointer",
            }}
          />
        ))}
      </Box>

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ "& th": { fontWeight: 700, backgroundColor: "#f8fafc" } }}>
              <TableCell>Driver</TableCell>
              <TableCell>Company</TableCell>
              <TableCell align="center">Step</TableCell>
              <TableCell align="center">Trainings</TableCell>
              <TableCell align="center">Policies</TableCell>
              <TableCell>Missing items</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredDrivers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
                  {data?.totalDrivers === 0
                    ? "No drivers are currently servicing this broker. Add a contract under Contracts first."
                    : "No drivers match the current filter."}
                </TableCell>
              </TableRow>
            ) : (
              filteredDrivers.map((r) => (
                <TableRow key={r.driverDetailsId} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.fullName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {r.email || r.phone || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{r.customerCompanyName || "—"}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      size="small"
                      label={r.onboardingStep ? r.onboardingStep.replace(/_/g, " ") : "—"}
                      sx={{ height: 20, fontSize: 11 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: r.passedRequiredTrainings === data?.requiredTrainingCount ? "#16a34a" : "#dc2626",
                      }}
                    >
                      {r.passedRequiredTrainings} / {data?.requiredTrainingCount ?? 0}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: r.signedRequiredPolicies === data?.requiredPolicyCount ? "#16a34a" : "#dc2626",
                      }}
                    >
                      {r.signedRequiredPolicies} / {data?.requiredPolicyCount ?? 0}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 280 }}>
                    {[...(r.missingTrainingCodes || []), ...(r.missingPolicyCodes || [])].length === 0 ? (
                      <Typography variant="caption" color="text.secondary">—</Typography>
                    ) : (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {(r.missingTrainingCodes || []).map((code) => (
                          <Tooltip key={`t-${code}`} title="Training missing">
                            <Chip
                              size="small"
                              label={code}
                              sx={{ height: 18, fontSize: 10, backgroundColor: "#fef3c7", color: "#92400e" }}
                            />
                          </Tooltip>
                        ))}
                        {(r.missingPolicyCodes || []).map((code) => (
                          <Tooltip key={`p-${code}`} title="Policy unsigned">
                            <Chip
                              size="small"
                              label={code}
                              sx={{ height: 18, fontSize: 10, backgroundColor: "#fee2e2", color: "#991b1b" }}
                            />
                          </Tooltip>
                        ))}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      size="small"
                      label={r.fullyCompliant ? "Compliant" : "Gaps"}
                      sx={{
                        backgroundColor: r.fullyCompliant ? "#dcfce7" : "#fee2e2",
                        color: r.fullyCompliant ? "#166534" : "#991b1b",
                        fontWeight: 600,
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Open driver compliance page">
                      <Button
                        size="small"
                        onClick={() => navigate(`/compliance/drivers/${r.driverDetailsId}`)}
                        endIcon={<OpenInNewIcon fontSize="small" />}
                      >
                        Open
                      </Button>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
};

export default BrokerCompliancePage;
